import React from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../actions/allActions";
import { FormDate } from "../../components/form/FormDate";
import FormInput from "../../components/form/FormInput";
import FormTextArea from "../../components/form/FormTextArea";
import { ErrorFetching } from "../../components/modals/ErrorFetching";
import { createEventObject } from "../../types/types";
import * as fState from "../../reducers/formReducer";
import * as eState from "../../reducers/errorReducer";
import { useNotify } from "../../hooks/useNotify";
import BreadCrumb from "../../components/breadcrumbs/BreadCrumb";
import { BreadRoute } from "../../components/breadcrumbs/BreadRoute";

export const CreateEventPage: React.FC = () => {
  const formName = "createNewEvent";
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, control } = useForm();
  const formState = useSelector(fState.getCreateEventFormInputs);
  const { message } = useSelector(eState.getErrorMessage);
  const errorMsg = useNotify(message);
  const styles =
    "w-full border border-gray-300 divide-y divide-gray-300 overflow-auto scroller relative shadow-xl rounded";
  const errorDivStyles =
    "absolute z-50 transform translate-y-1/2 translate-x-1/2 top-0 left-0 flex justify-center place-items-center  w-1/2 h-1/6 shadow-xl bg-white border-none rounded";
  const inputContainers = "w-1/3";
  const eventStartId = "eventStartDate";
  const eventEndId = "eventEndDate";
  const eventStart = formState[eventStartId] as string;
  const eventEnd = formState[eventEndId] as string;
  const eventStartDateValue =
    eventStart === undefined || eventStart === null
      ? null
      : new Date(eventStart);
  const eventEndDateValue =
    eventEnd === undefined || eventEnd === null ? null : new Date(eventEnd);
  const eventNameId = "eventName";
  const eventNameValue = formState[eventNameId] as string;
  const eventOrganiserId = "eventOrganiser";
  const eventOrganiserValue = formState[eventOrganiserId] as string;
  const eventDescriptionId = "eventDescription";
  const eventDescriptionValue = formState[eventDescriptionId] as string;
  const eventPlaceId = "eventPlace";
  const eventPlaceValue = formState[eventPlaceId] as string;
  const eventCityId = "eventCity";
  const eventCityValue = formState[eventCityId] as string;
  const eventZipId = "eventZip";
  const eventZipValue = formState[eventZipId] as string;
  const eventStreetAddressId = "eventStreetAddress";
  const eventStreetAddressValue = formState[eventStreetAddressId] as string;
  const eventCountryId = "eventCountry";
  const eventCountryValue = formState[eventCountryId] as string;
  const supportEmailId = "supportEmail";
  const supportEmailValue = formState[supportEmailId] as string;
  const eventWebPageId = "eventWebPage";
  const eventWebPageValue = formState[eventWebPageId] as string;
  const supportPhoneId = "supportPhone";
  const supportPhoneValue = formState[supportPhoneId] as string;
  const supportInstructionId = "supportInstruction";
  const supportInstructionValue = formState[supportInstructionId] as string;

  const create = (data: createEventObject) => {
    dispatch(allActions.eventActions.createEvent(data));
  };

  const handleInputChange = (event: any, id: string, type?: string) => {
    if (type === "array") {
      const currentValue: string[] | string = formState[id];
      const newArray =
        currentValue === undefined
          ? []
          : typeof currentValue === "string"
          ? [currentValue]
          : currentValue;
      dispatch(
        allActions.formActions.addToValue(
          id,
          newArray,
          event.target.value,
          formName
        )
      );
    } else {
      dispatch(
        allActions.formActions.updateValue(id, event.target.value, formName)
      );
    }
  };

  const resetDate = (e: any, id: string) => {
    dispatch(allActions.formActions.updateDateValue(id, null, formName));
  };

  const handleDateChange = (e: any, date: Date, id: string) => {
    if (date) {
      dispatch(allActions.formActions.updateDateValue(id, date, formName));
    }
  };

  const buttonInfo = {
    style:
      "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-20",
    format_id: "CreateEvent.create_button",
  };

  const useFormatMessage = (messageId: string) => {
    return useIntl().formatMessage({ id: messageId });
  };

  return (
    <div className='h-screen container'>
      <BreadRoute>
        <BreadCrumb label={"menu"} link={"/eventmenu"} />
        <BreadCrumb label={"create"} link={"/createevent"} lastChild={true} />
      </BreadRoute>
      <div className='flex h-1/3 min-h-1/3 justify-center'>
        <div className='h-full flex flex-col justify-center'>
          <div className='h-1/6'>
            <span className='semibold text-3xl text-brand-dark_blue'>
              <FormattedMessage id={"Event.Create.Header"} />
            </span>
          </div>
          <div className='h-1/6'>
            <span className='text-xl text-brand-dark_blue'>
              <FormattedMessage id={"Event.Create.Paragraph"} />
            </span>
          </div>
        </div>
        <div className='w-1/2 h-full'>
          <img
            className='h-full min-h-full'
            src={`${process.env.PUBLIC_URL}/img/5217.jpg`}
            alt={`image_creating`}
          />
        </div>
      </div>
      <form className={styles} onSubmit={handleSubmit(create)}>
        <div className='flex justify-evenly'>
          <FormInput
            id={eventNameId}
            question={useFormatMessage("Form.CreateEvent.Name")}
            value={eventNameValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormTextArea
            id={eventDescriptionId}
            question={useFormatMessage("Form.CreateEvent.Description")}
            value={eventDescriptionValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormInput
            id={eventOrganiserId}
            question={useFormatMessage("Form.CreateEvent.Organiser")}
            value={eventOrganiserValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
        </div>
        <div className='flex justify-evenly divide-x divide-gray-200'>
          <FormInput
            id={eventPlaceId}
            question={useFormatMessage("Form.CreateEvent.Place")}
            value={eventPlaceValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormInput
            id={eventStreetAddressId}
            question={useFormatMessage("Form.CreateEvent.StreetAddress")}
            value={eventStreetAddressValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormInput
            id={eventZipId}
            question={useFormatMessage("Form.CreateEvent.Zip")}
            value={eventZipValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormInput
            id={eventCityId}
            question={useFormatMessage("Form.CreateEvent.City")}
            value={eventCityValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
          <FormInput
            id={eventCountryId}
            question={useFormatMessage("Form.CreateEvent.Country")}
            value={eventCountryValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_any` })}
            errors={errors}
          />
        </div>
        <div className='flex justify-center'>
          <FormDate
            id={eventStartId}
            question={useFormatMessage("Form.CreateEvent.Start")}
            value={eventStartDateValue}
            handleInputChange={handleDateChange}
            containerStyle={inputContainers}
            register={register({ required: `Form.required_any` })}
            required={true}
            reset={resetDate}
            errors={errors}
            control={control}
          />
          <FormDate
            id={eventEndId}
            question={useFormatMessage("Form.CreateEvent.End")}
            value={eventEndDateValue}
            handleInputChange={handleDateChange}
            containerStyle={inputContainers}
            register={register({ required: `Form.required_any` })}
            required={true}
            reset={resetDate}
            errors={errors}
            control={control}
          />
        </div>
        <div className='flex justify-evenly'>
          <FormInput
            id={eventWebPageId}
            question={useFormatMessage("Form.CreateEvent.Webpage")}
            value={eventWebPageValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register()}
            errors={errors}
          />
          <FormInput
            id={supportEmailId}
            question={useFormatMessage("Form.CreateEvent.SupportEmail")}
            value={supportEmailValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register({ required: `Form.required_email` })}
            errors={errors}
          />
          <FormInput
            id={supportPhoneId}
            question={useFormatMessage("Form.CreateEvent.SupportPhone")}
            value={supportPhoneValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register()}
            errors={errors}
          />
          <FormInput
            id={supportInstructionId}
            question={useFormatMessage("Form.CreateEvent.SupportInstruction")}
            value={supportInstructionValue || ""}
            handleInputChange={handleInputChange}
            containerStyle={inputContainers}
            type='text'
            register={register()}
            errors={errors}
          />
        </div>
        <ErrorFetching message={errorMsg} styles={errorDivStyles} />
        <button type='submit' className={buttonInfo.style}>
          <FormattedMessage id={buttonInfo.format_id} />
        </button>
      </form>
    </div>
  );
};
